import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { lumi } from '../lib/lumi'
import HeaderV2 from '../components/HeaderV2'
import FooterV2 from '../components/FooterV2'
import ShopHeroV2 from './shop/v2/ShopHeroV2'
import ShopToolbarV2, { type CategoryKey } from './shop/v2/ShopToolbarV2'
import ProductCardV2 from './shop/v2/ProductCardV2'
import type { ProductV2, SortKey } from './shop/v2/types'
import BeansSchemaV2 from './shop/v2plus/BeansSchemaV2'
import BeansStoriesV2 from './shop/v2plus/BeansStoriesV2'
import BeansFAQV2 from './shop/v2plus/BeansFAQV2'

/**
 * ShopBeansV2Page (Universal Shop V2++)
 *
 * 「Barista 純粹版」策略：
 *  - 表面克制：一張圖 + 大字 + 商品 grid + 兩個全收手風琴 + 黑底頁尾
 *  - 底層飽滿：sr-only H1、Helmet meta、JSON-LD（Org / Breadcrumb / CollectionPage / ItemList / Product / FAQPage / Speakable）
 *  - SEO 100% 對齊舊版 ShopPage，視覺乾淨如 Barista Map
 *
 * 路由：/{lang}/shop/v2、/{lang}/shop/:category/v2
 */
const ShopBeansV2Page: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const params = useParams<{ category?: string }>()
  const currentLang: 'zh' | 'en' = location.pathname.startsWith('/en') ? 'en' : 'zh'
  const isZh = currentLang === 'zh'

  // Inject Barista Map fonts
  useEffect(() => {
    const id = 'baristamap-fonts'
    if (document.getElementById(id)) return
    const link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    link.href =
      'https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Noto+Serif+TC:wght@200;300;400&display=swap'
    document.head.appendChild(link)
  }, [])

  const resolveCategory = (raw?: string, pathname?: string): CategoryKey => {
    const c = (raw || '').toLowerCase()
    if (c === 'beans' || c === 'dripbag' || c === 'gift' || c === 'gear') return c
    // 路由 /zh|en/shop/beans/v2 沒有 :category 參數，從 pathname 偵測
    const path = (pathname || '').toLowerCase()
    if (path.includes('/shop/beans/')) return 'beans'
    if (path.includes('/shop/dripbag/')) return 'dripbag'
    if (path.includes('/shop/gift/')) return 'gift'
    if (path.includes('/shop/gear/')) return 'gear'
    return 'all'
  }
  const [category, setCategory] = useState<CategoryKey>(
    resolveCategory(params.category, location.pathname)
  )

  useEffect(() => {
    setCategory(resolveCategory(params.category, location.pathname))
  }, [params.category, location.pathname])

  const handleCategoryChange = (c: CategoryKey) => {
    setCategory(c)
    const base = `/${currentLang}/shop`
    const next = c === 'all' ? `${base}/v2` : `${base}/${c}/v2`
    navigate(next)
  }

  const [products, setProducts] = useState<ProductV2[]>([])
  const [loading, setLoading] = useState(true)
  const [sort, setSort] = useState<SortKey>('featured')

  // SAMPLE: 顯示所有商品（含未啟用），方便預覽版面
  // TODO: 正式上線改為 filter: { status: 'active', category }
  useEffect(() => {
    setLoading(true)
    lumi.entities.products
      ?.list?.({
        limit: 100,
        sort: { isFeatured: -1, createdAt: -1 },
      })
      .then((res: any) => {
        const all: ProductV2[] = res?.list || []
        if (category === 'all') {
          setProducts(all)
          return
        }
        const filtered = all.filter(
          (p) => (p.category || '').toLowerCase() === category
        )
        setProducts(filtered.length > 0 ? filtered : all)
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false))
  }, [category])

  const sortedProducts = useMemo(() => {
    const arr = [...products]
    switch (sort) {
      case 'price-asc':
        return arr.sort((a, b) => (a.price || 0) - (b.price || 0))
      case 'price-desc':
        return arr.sort((a, b) => (b.price || 0) - (a.price || 0))
      case 'newest':
        return arr.sort((a, b) =>
          (b.createdAt || '').localeCompare(a.createdAt || '')
        )
      case 'featured':
      default:
        return arr.sort((a, b) => {
          const fa = a.isFeatured ? 1 : 0
          const fb = b.isFeatured ? 1 : 0
          if (fb !== fa) return fb - fa
          return (b.createdAt || '').localeCompare(a.createdAt || '')
        })
    }
  }, [products, sort])

  // SEO H1（畫面隱藏，搜尋引擎與輔助工具可讀）
  const srH1 = isZh
    ? '咖啡豆推薦｜精品咖啡豆網購・自家烘焙｜ONGU Coffee'
    : 'Specialty Coffee Beans | Home Roasting | ONGU Coffee'

  return (
    <div className="min-h-screen bg-white">
      {/* SEO 結構化資料（僅 beans 分類注入完整 Schema，對齊舊版） */}
      <BeansSchemaV2 lang={currentLang} category={category} products={products} />

      {/* sr-only H1：畫面看不到，但 SEO 可讀 */}
      <h1 className="sr-only">{srH1}</h1>

      <HeaderV2 />
      <div className="h-20" />

      <ShopHeroV2 lang={currentLang} category={category} />

      <main className="mx-auto max-w-[1600px] px-5 md:px-10 pt-16 md:pt-20 pb-24">
        <ShopToolbarV2
          count={sortedProducts.length}
          sort={sort}
          onSortChange={setSort}
          category={category}
          onCategoryChange={handleCategoryChange}
          currentLang={currentLang}
        />

        {loading ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-5 sm:gap-y-12 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i}>
                <div className="aspect-square w-full bg-black/[0.04]" />
                <div className="mt-4 h-3 w-3/4 bg-black/[0.04]" />
                <div className="mt-2 h-3 w-1/3 bg-black/[0.04]" />
              </div>
            ))}
          </div>
        ) : sortedProducts.length === 0 ? (
          <div
            className="py-20 text-center text-sm text-black/50"
            style={{ letterSpacing: '0.05em' }}
          >
            {isZh ? '暫無商品' : 'No products yet'}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-5 sm:gap-y-12 md:grid-cols-3 lg:grid-cols-4">
            {sortedProducts.map((p) => (
              <ProductCardV2 key={p._id} product={p} currentLang={currentLang} />
            ))}
          </div>
        )}
      </main>

      {/* 兩個極簡手風琴（預設全收）：Stories（12 篇）+ FAQ（12 題） */}
      {/* beans 或 all（總覽）皆顯示，對齊舊版 ShopPage 行為 */}
      {(category === 'beans' || category === 'all') && (
        <>
          <BeansStoriesV2 lang={currentLang} />
          <BeansFAQV2 lang={currentLang} />
        </>
      )}

      {/* sr-only：完整 SEO 文字內容（畫面看不到，搜尋引擎可讀） */}
      {(category === 'beans' || category === 'all') && isZh && (
        <div className="sr-only" aria-hidden="true">
          <h2>ONGU Coffee 咖啡豆網購推薦重點</h2>
          <p>
            每一支 ONGU Coffee 咖啡豆皆由烘豆師少量烘焙、反覆杯測，追求乾香、濕香與入口層次的平衡。
            線上咖啡豆網購專區同步實體 ONGU Studio 風味標準，讓你無論在店內或在家沖煮，都能喝到一貫的質感與細緻度。
          </p>
          <h2>如何挑選適合你的咖啡豆風味</h2>
          <p>
            喜歡果酸明亮、花香細緻，可以從淺焙或中淺焙單品開始；偏好厚實苦甜、適合拿鐵與冰咖啡，則建議選擇中深焙或義式配方。
            也可以依照商品標籤上的風味描述（果香、堅果、巧克力等）來嘗試，找到屬於你的日常口糧豆。
          </p>
          <h2>烘焙度與沖煮方式建議</h2>
          <p>
            濾掛與手沖建議以淺焙到中焙咖啡豆為主，呈現產區特色與乾淨度；
            若使用義式機、摩卡壺或全自動咖啡機，則可選擇中深焙或專為義式設計的配方豆。
          </p>
          <h2>ONGU Studio 現場咖啡豆</h2>
          <p>
            想親眼看見咖啡豆從生豆到出爐的瞬間變化？歡迎造訪 ONGU Studio 實體店，現場不定期進行烘豆與杯測。
          </p>
          <h2>咖啡豆保存與磨豆小提醒</h2>
          <p>
            建議優先購買整豆，沖煮前再現磨風味最佳。開封後請密封保存、避免陽光直射與高溫潮濕，
            一般建議於烘焙後 7–30 天內為最佳賞味期。
          </p>
          <h2>ONGU Coffee 咖啡豆宅配與出貨說明</h2>
          <p>
            線上完成訂單後，我們會於 1–3 個工作天內依序出貨，並由合作物流配送至指定地址。
          </p>
        </div>
      )}

      <FooterV2 />
    </div>
  )
}

export default ShopBeansV2Page
